import { EventAPI } from '@praxis/analytics'
import moment from 'moment-timezone'

import {
  generateSessionId,
  generateTaskId,
  resetTaskId,
  sessionId,
  taskId,
  trackSession,
} from './initializeFirefly'
import config from '../config/apiConfig'
import { searchFilterKey } from './firefly'
import { recursiveToCamel } from '../betasearch/mappers/searchMapper'
import { DOWNLOAD_ORIGINAL_VERSION } from '../constants/search'

export const BetaAnalytics = {
  event: {
    LOAD_TIME: 'PAGE_VIEW',
    HOMEPAGE: 'HOMEPAGE',
    BANNER_CLICKED: 'BANNER_CLICKED',
    SEARCH_TEXT: 'SEARCH_TEXT',
    REFRESH_SEARCH: 'REFRESH_SEARCH',
    REVERSE_IMAGE_SEARCH: 'REVERSE_IMAGE_SEARCH',
    ADD_FILTER: 'ADD_FILTER',
    REMOVE_FILTER: 'REMOVE_FILTER',
    REMOVE_ALL_FILTERS: 'REMOVE_ALL_FILTERS',
    FILTER_UPLOAD_DATE: 'UPLOAD_DATE',
    FILTER_ASSET_DESCRIPTION: 'ASSET_DESCRIPTION',
    FILTER_COLOR: 'COLOR',
    FILTER_IS_ARCHIVED: 'IS_ARCHIVED',
    FILTER_INCLUDE_ARCHIVED: 'INCLUDE_ARCHIVED',
    FILTER_DELETED_ASSETS: 'DELETED_ASSETS',
    FILTER_IS_REUSABLE: 'IS_REUSABLE',
    FILTER_IS_METADATA_COMPLETE: 'IS_METADATA_COMPLETE',
    FILTER_LIBRARIAN: 'LIBRARIAN_FILTER',
    FILTER_FILE_TYPE: 'File Type',
    FILTER_CLASSIFICATION: 'Classification',
    FILTER_ASSET_TYPE: 'Asset Type',
    FILTER_AI_CLASSIFICATION: 'AI Classification',
    FILTER_CHANNEL: 'Channel',
    FILTER_BRAND: 'Brand',
    FILTER_DEPARTMENT: 'Department',
    FILTER_CLASS: 'Class',
    FILTER_REUSE_READY: 'IS_REUSABLE',
    FILTER_IS_GEN_AI_READY: 'IS_GEN_AI_READY',
    TEMPLATE: 'TEMPLATE',
    SORT_PREFERENCE: 'SORT_PREFERENCE',
    PAGE_CHANGE: 'PAGE_CHANGE',
    ROWS_PER_PAGE_CHANGE: 'ROWS_PER_PAGE_CHANGE',
    ASSET_USAGE: 'ASSET_USAGE',
    VIEW_ASSET_DETAILS: 'VIEW_ASSET_DETAILS',
    AI_ENABLED: 'ENABLED_FOR_AI_TESTING',
    RESTORE_DELETED: 'RESTORE_DELETED',
    DOWNLOAD_METADATA: 'DOWNLOAD_METADATA',
    ADD_ASSETS_TO_BOARDS: 'ADD_ASSETS_TO_BOARDS',
    DELETE_ASSET: 'DELETE_ASSET',
    DOWNLOAD_ASSET: 'DOWNLOAD_ASSET',
    DOWNLOAD_ASSET_ZIP: 'DOWNLOAD_ASSET_ZIP',
    CREATE_ASSET_ZIP: 'CREATE_ASSET_ZIP',
    DOWNLOAD_ASSET_PROJECTS: 'DOWNLOAD_ASSET_PROJECTS',
    DOWNLOAD_ASSET_BOARDS: 'DOWNLOAD_ASSET_BOARDS',
    BULK_DOWNLOAD_ASSETS: 'BULK_DOWNLOAD_ASSETS',
    SINGLE_ASSET_DETAIL_PAGE: 'SINGLE_ASSET_DETAIL_PAGE',
    SINGLE_UPDATE_METADATA: 'SINGLE_UPDATE_METADATA',
    BULK_UPDATE_METADATA: 'BULK_UPDATE_METADATA',
    SEARCH_RESPONSE_INSIGHTS: 'SEARCH_RESPONSE_INSIGHTS',
    //archived/unarchived asset
    ARCHIVED_ASSET: 'ARCHIVED_ASSET',
    UNARCHIVED_ASSET: 'UNARCHIVED_ASSET',
    //For AI terms & conditions
    ACCEPT_AI_TERMS: 'ACCEPT_AI_TERMS',
  },
  SCHEMA_VERSION: '1.40.0',
}

const TIMESTAMP_ISO_1806 = 'YYYY-MM-DD HH:mm:ss Z'
const ASSETHUB_UI = 'ASSETHUB_UI'
export const BETA_ANALYTICS_DELIMITER = ','
export let prevEventType = ''

// Initialize Praxis Analytics for Beta Search
const betaAnalyticsConfig = {
  appName: ASSETHUB_UI,
  apiKey: config?.analytics?.key || 'test-key',
  url: config?.analytics?.url,
}
const betaAnalyticsInstance = new EventAPI(betaAnalyticsConfig)

const getCommonFireflyData = (auth = {}) => {
  const { lanId = '', email = '' } = auth
  if (!taskId || !sessionId) {
    generateTaskId(lanId)
  }
  return {
    user: {
      customDimension1: email,
      taskId: lanId,
      segment: taskId,
    },
  }
}

const sendFireflyEvent = (
  authData = {},
  trackingData = {},
  fireflyGlobalData = {},
) => {
  const { lanId = '' } = authData
  const { eventType = '', eventName = {} } = trackingData
  let eventTimestamp = moment.utc().format(TIMESTAMP_ISO_1806)
   if (!sessionId) {
    generateSessionId(lanId)
  }
  trackSession(lanId, eventTimestamp)
  const eventData = {
    type: eventType,
    name: eventName,
    customSessionId: sessionId,
  }

  betaAnalyticsInstance.sendEvent({
    event: eventData,
    ...fireflyGlobalData,
  })
  prevEventType = eventType
}

export const trackBetaSearchAnalytics = ({ auth = {}, trackingData = {} }) => {
  const { eventName = '' } = trackingData
  switch (eventName) {
    case BetaAnalytics.event.SEARCH_TEXT:
      trackSearchTextClick({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.FILTER_UPLOAD_DATE:
    case BetaAnalytics.event.FILTER_IS_ARCHIVED:
    case BetaAnalytics.event.FILTER_IS_REUSABLE:
    case BetaAnalytics.event.FILTER_IS_METADATA_COMPLETE:
    case BetaAnalytics.event.FILTER_INCLUDE_ARCHIVED:
    case BetaAnalytics.event.FILTER_DELETED_ASSETS:
    case BetaAnalytics.event.FILTER_FILE_TYPE:
    case BetaAnalytics.event.FILTER_CLASSIFICATION:
    case BetaAnalytics.event.TEMPLATE:
    case BetaAnalytics.event.FILTER_CHANNEL:
    case BetaAnalytics.event.FILTER_BRAND:
    case BetaAnalytics.event.FILTER_DEPARTMENT:
    case BetaAnalytics.event.FILTER_CLASS:
    case BetaAnalytics.event.REMOVE_FILTER:
    case BetaAnalytics.event.REMOVE_ALL_FILTERS:
    case BetaAnalytics.event.ROWS_PER_PAGE_CHANGE:
    case BetaAnalytics.event.PAGE_CHANGE:
    case BetaAnalytics.event.SORT_PREFERENCE:
    case BetaAnalytics.event.AI_ENABLED:
    case BetaAnalytics.event.RESTORE_DELETED:
    case BetaAnalytics.event.BANNER_CLICKED:
    case BetaAnalytics.event.HOMEPAGE:
    case BetaAnalytics.event.DOWNLOAD_METADATA:
    case BetaAnalytics.event.VIEW_ASSET_DETAILS:
      trackEventWithCustomInteraction({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.FILTER_ASSET_DESCRIPTION:
      trackAssetDescriptionFilter({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.FILTER_AI_CLASSIFICATION:
      trackAIClassificationFilter({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.FILTER_COLOR:
      trackColorFilter({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.REFRESH_SEARCH:
      trackRefreshSearch({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.REVERSE_IMAGE_SEARCH:
      trackReverseImageSearch({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.ADD_ASSETS_TO_BOARDS:
      trackAddAssetsToBoard({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.DELETE_ASSET:
      trackAssetDelete({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.DOWNLOAD_ASSET_PROJECTS:
    case BetaAnalytics.event.DOWNLOAD_ASSET_BOARDS:
    case BetaAnalytics.event.DOWNLOAD_ASSET:
      trackAssetDownload({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.CREATE_ASSET_ZIP:
      trackZipCreation({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.DOWNLOAD_ASSET_ZIP:
      trackZipDownload({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.ARCHIVED_ASSET:
    case BetaAnalytics.event.UNARCHIVED_ASSET:
      trackAssetArchived({
        auth,
        trackingData,
      })
      return
    case BetaAnalytics.event.ACCEPT_AI_TERMS:
      trackEventWithCustomInteraction({
        auth,
        trackingData,
      })
      return
    default:
      trackEventWithCustomInteraction({
        auth,
        trackingData,
      })
  }
}

export const trackSearchTextClick = ({ auth = {}, trackingData = {} }) => {
  resetTaskId()
  const { eventData = {} } = trackingData
  const { searchTerm, searchType } = eventData
  const customInteraction = {
    key: searchType === 'ALL' ? searchFilterKey(searchTerm) : searchType,
    value: searchTerm,
  }
  const fireflyCommonData = getCommonFireflyData(auth)
  const fireflyGlobalData = { ...fireflyCommonData, customInteraction }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
}

export const trackEventWithCustomInteraction = ({
  auth = {},
  trackingData = {},
}) => {
  const { eventData = {}, eventName = '' } = trackingData
  const { key = '', value = '' } = eventData
  const fireflyCommonData = getCommonFireflyData(auth)
  const customInteraction = {
    key,
    value: value?.toString(),
  }
  const fireflyGlobalData = { ...fireflyCommonData, customInteraction }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
  if (
    eventName === BetaAnalytics.event.DOWNLOAD_METADATA ||
    eventName === BetaAnalytics.event.VIEW_ASSET_DETAILS
  ) {
    resetTaskId()
  }
}

export const trackRefreshSearch = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData
  const fireflyCommonData = getCommonFireflyData(auth)
  const customInteraction = {
    key: 'EXISTING_SEARCH_PAYLOAD',
    value: JSON.stringify(eventData),
  }
  const fireflyGlobalData = { ...fireflyCommonData, customInteraction }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
  resetTaskId()
}

export const trackAssetDescriptionFilter = ({
  auth = {},
  trackingData = {},
}) => {
  const { eventData = {} } = trackingData
  const fireflyCommonData = getCommonFireflyData(auth)
  let customMetrics = {}
  if (eventData['metadata.imageData.people']) {
    customMetrics['metric1'] = String(eventData['metadata.imageData.people'])
  }
  if (eventData['metadata.imageData.withFood']) {
    customMetrics['metric2'] = String(eventData['metadata.imageData.withFood'])
  }
  if (eventData['metadata.imageData.animals']) {
    customMetrics['metric3'] = String(eventData['metadata.imageData.animals'])
  }

  const fireflyGlobalData = { ...fireflyCommonData, customMetrics }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
}

export const trackAIClassificationFilter = ({
  auth = {},
  trackingData = {},
}) => {
  const { eventData = {} } = trackingData
  const fireflyCommonData = getCommonFireflyData(auth)
  let customMetrics = {}
  customMetrics['metric1'] = String(eventData.classifications.join(
    BETA_ANALYTICS_DELIMITER,
  ))
  customMetrics['metric2'] = String(eventData.isTalentFilter)
  const fireflyGlobalData = { ...fireflyCommonData, customMetrics }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
}

export const trackColorFilter = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData
  const fireflyCommonData = getCommonFireflyData(auth)
  let customMetrics = {}
  if (eventData['metadata.imageData.colorBuckets.bucket.nested']) {
    customMetrics['metric1'] = String(
      eventData['metadata.imageData.colorBuckets.bucket.nested']
    )
  }
  if (eventData['metadata.imageData.colors.hex.nested']) {
    customMetrics['metric2'] = String(eventData['metadata.imageData.colors.hex.nested'])
  }
  if (eventData['metadata.imageData.colors.hsv.nested']) {
    customMetrics['metric3'] = String(eventData['metadata.imageData.colors.hsv.nested'])
  }

  const fireflyGlobalData = { ...fireflyCommonData, customMetrics }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
}

export const trackReverseImageSearch = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData

  const fireflyCommonData = getCommonFireflyData(auth)

  let customMetrics = {}
  customMetrics['metric1'] = String(eventData.fileName)
  customMetrics['metric2'] = String(eventData.fileSize)
  customMetrics['metric3'] = String(eventData.fileType)

  const fireflyGlobalData = { ...fireflyCommonData, customMetrics }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
  resetTaskId()
}

export const trackZipCreation = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData
  const {
    source = '',
    downloadType = '',
    totalSize = '',
    assets = [],
    jobId = '',
  } = eventData
  const fireflyCommonData = getCommonFireflyData(auth)
  let customMetrics = {}
  customMetrics['metric1'] = String(source)
  customMetrics['metric2'] = String(downloadType)
  customMetrics['metric3'] = String(totalSize)
  customMetrics['metric4'] = assets?.length || 0

  const customInteraction = {
    key: 'job_id',
    value: jobId,
  }

  const fireflyGlobalData = {
    ...fireflyCommonData,
    customMetrics,
    customInteraction,
  }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
}

export const trackZipDownload = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData
  const {
    job_id = '',
    download_type = '',
    requested_at = '',
    completed_at = '',
    total_asset_count = 0,
    successful_assets_count = 0,
    failed_asset_count = 0,
  } = eventData
  const fireflyCommonData = getCommonFireflyData(auth)
  let customMetrics = {}
  customMetrics['metric1'] = String(download_type)
  customMetrics['metric2'] = String(requested_at)
  customMetrics['metric3'] = String(completed_at)
  customMetrics['metric4'] = total_asset_count
  customMetrics['metric5'] = successful_assets_count
  customMetrics['metric6'] = failed_asset_count

  const customInteraction = {
    key: 'job_id',
    value: job_id,
  }

  const fireflyGlobalData = {
    ...fireflyCommonData,
    customMetrics,
    customInteraction,
  }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
  resetTaskId()
}

const trackAddAssetsToBoard = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData
  const { payload = {}, assetInfo = [] } = eventData
  const fireflyCommonData = getCommonFireflyData(auth)

  Object.values(payload)?.flat(1).length &&
    Object.values(payload)
      .flat(1)
      .forEach((assetDetails) => {
        const customInteraction = {
          key: 'board_id',
          value: Object.keys(payload)?.join(BETA_ANALYTICS_DELIMITER),
          location: assetDetails?.asset_id || '',
        }
        let customMetrics = {}
        const mappedAssetInfo =
          assetInfo.find((info) => info.assetId === assetDetails?.asset_id) ||
          {}
        customMetrics['metric1'] = String(mappedAssetInfo?.projectInfo?.channel || '')
        customMetrics['metric2'] = String(mappedAssetInfo?.masterChannel || '')
        customMetrics['metric3'] = String(
          mappedAssetInfo?.assetClassification?.classificationCode || ''
        )
        customMetrics['metric5'] =
          mappedAssetInfo?.isReusable === ''
            ? 0
            : mappedAssetInfo?.isReusable === false
              ? 1
              : 2
        const fireflyGlobalData = {
          ...fireflyCommonData,
          customMetrics,
          customInteraction,
        }
        sendFireflyEvent(auth, trackingData, fireflyGlobalData)
      })
  resetTaskId()
}

const trackAssetDelete = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData
  const { payload = {} } = eventData
  const fireflyCommonData = getCommonFireflyData(auth)
  let customMetrics = {}
  const { asset_uoi_ids = [], asset_master_uoi_ids = [] } = payload
  if (asset_uoi_ids.length) {
    customMetrics['metric1'] = String(asset_uoi_ids.join(BETA_ANALYTICS_DELIMITER))
  }
  if (asset_master_uoi_ids.length) {
    customMetrics['metric2'] = String(asset_master_uoi_ids.join(
      BETA_ANALYTICS_DELIMITER,
    ))
  }
  const fireflyGlobalData = {
    ...fireflyCommonData,
    customMetrics,
  }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
  resetTaskId()
}

const trackAssetArchived = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {} } = trackingData
  const { payload = {} } = eventData
  const fireflyCommonData = getCommonFireflyData(auth)
  let customMetrics = {}

  if (payload.length) {
    customMetrics['metric1'] = String(payload.join(BETA_ANALYTICS_DELIMITER))
  }
  const fireflyGlobalData = {
    ...fireflyCommonData,
    customMetrics,
  }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
  resetTaskId()
}

const constructAssetDetails = (eventData = {}, eventName = '') => {
  const { asset = {}, downloadStyle = '' } = eventData
  const assetData = recursiveToCamel(asset) || {}
  const {
    fileMetadata = {},
    projectInfo: metadataProjectInfo = {},
    assetClassification: metadataAssetClassification = {},
    masterChannel: metadataMasterChannel = '',
  } = assetData?.metadata || {}
  const usesNestedMetadata =
    downloadStyle === BetaAnalytics.event.SINGLE_ASSET_DETAIL_PAGE ||
    eventName === BetaAnalytics.event.DOWNLOAD_ASSET_BOARDS ||
    eventName === BetaAnalytics.event.DOWNLOAD_ASSET_PROJECTS

  if (usesNestedMetadata) {
    assetData['fileName'] = fileMetadata?.fileName || ''
    assetData['projectInfo'] = metadataProjectInfo || {}
    assetData['itemInfo'] = assetData?.metadata?.itemInfo || []
    assetData['usageInfo'] = assetData?.metadata?.usageInfo || []
    assetData['assetClassification'] = metadataAssetClassification || {}
    assetData['isReusable'] = assetData?.isReusable || ''
  } else {
    assetData['projectInfo'] = {
      ...(metadataProjectInfo || {}),
      ...(assetData?.projectInfo || {}),
    }
    assetData['assetClassification'] = {
      ...(metadataAssetClassification || {}),
      ...(assetData?.assetClassification || {}),
    }
  }
  assetData['masterChannel'] =
    assetData?.masterChannel || metadataMasterChannel || ''
  return assetData
}

const trackAssetDownload = ({ auth = {}, trackingData = {} }) => {
  const { eventData = {}, eventName = '' } = trackingData
  const { asset = {}, downloadResolution = '', downloadStyle = '' } = eventData
  const assetDetails = constructAssetDetails(eventData, eventName)
  const {
    assetId = '',
    fileName = '',
    isDerivative = '',
    isReusable = '',
    masterChannel = '',
    projectInfo = {},
    itemInfo = [],
    usageInfo = [],
    assetClassification = {},
  } = assetDetails

  let itemBrands = []
  let itemMerchandiseGroupNames = []

  itemInfo.forEach((itemDetails) => {
    const { brand = '', merchandiseClassification = {} } = itemDetails
    const { groupName = '' } = merchandiseClassification || {}
    if (brand) {
      itemBrands.push(brand)
    }
    if (groupName) {
      itemMerchandiseGroupNames.push(groupName)
    }
  })
  const usageList = usageInfo?.map((usageDetails) => usageDetails.drmId) || []
  const fireflyCommonData = getCommonFireflyData(auth)
  fireflyCommonData.user['customDimension2'] = itemBrands.length
    ? itemBrands.join(BETA_ANALYTICS_DELIMITER)
    : ''
  fireflyCommonData.user['customDimension3'] = itemMerchandiseGroupNames.length
    ? itemMerchandiseGroupNames.join(BETA_ANALYTICS_DELIMITER)
    : ''
  const customInteraction = {
    key: assetId,
    value: downloadStyle,
    location: fileName,
  }
  let customMetrics = {}
  customMetrics['metric1'] = String(projectInfo?.channel || '')
  customMetrics['metric2'] = String(masterChannel)
  customMetrics['metric3'] = String(
    assetClassification?.classificationCode || '',
  )
  customMetrics['metric4'] =
    downloadResolution === DOWNLOAD_ORIGINAL_VERSION ? 2 : 1
  customMetrics['metric5'] =
    isReusable === '' ? 0 : isReusable === false ? 1 : 2
  const fireflyGlobalData = {
    ...fireflyCommonData,
    customMetrics,
    customInteraction,
  }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
  if (downloadStyle !== BetaAnalytics.event.BULK_DOWNLOAD_ASSETS) {
    resetTaskId()
  }
}

const getEventNameBasedOnLocation = (location) => {
  switch (location) {
    case 'boards':
      return BetaAnalytics.event.DOWNLOAD_ASSET_BOARDS
    case 'projects':
      return BetaAnalytics.event.DOWNLOAD_ASSET_PROJECTS
    default:
      return BetaAnalytics.event.DOWNLOAD_ASSET
  }
}

export const trackBetaSearchBulkDownloads = (betaFireflyData = {}) => {
  const {
    selectedAssets = [],
    auth = {},
    downloadAssetType,
    location,
  } = betaFireflyData
  selectedAssets.forEach((assetDetails, index) => {
    const fireflyTrackingData = {
      eventType: BetaAnalytics.event.ASSET_USAGE,
      eventName: getEventNameBasedOnLocation(location),
      eventData: {
        asset: assetDetails,
        downloadResolution: downloadAssetType,
        downloadStyle: BetaAnalytics.event.BULK_DOWNLOAD_ASSETS,
      },
    }
    trackAssetDownload({ auth, trackingData: fireflyTrackingData })
    if (index === selectedAssets.length - 1) {
      resetTaskId()
    }
  })
}

export const trackUpdateMetadata = ({
  authData,
  metadataPayload,
  trackingData,
}) => {
  const { asset_id = '', update_data = {} } = metadataPayload
  const { eventName = {} } = trackingData
  const {
    asset_descriptions = {},
    creative_usage_rights = {},
    drm_ids = {},
    tcins = {},
    classification_id = '',
    file_name = '',
    librarian_review_status = '',
    user_defined_brand_name = '',
  } = update_data
  const { keywords = [], shot_types = [], subject = [] } = asset_descriptions
  const {
    additional_directions = '',
    do_not_use = '',
    instructions = '',
  } = creative_usage_rights
  const { data: drmData = [] } = drm_ids
  const { data: tcinData = [] } = tcins
  const fireflyCommonData = getCommonFireflyData(authData)
  if (drmData.length) {
    fireflyCommonData.user['customDimension2'] =
      drmData.join(BETA_ANALYTICS_DELIMITER) || ''
  }
  if (tcinData.length) {
    fireflyCommonData.user['customDimension3'] =
      tcinData.join(BETA_ANALYTICS_DELIMITER) || ''
  }
  let customInteraction = {
    key: asset_id,
    value: librarian_review_status,
    location: file_name,
  }
  let customMetrics = {}
  if (classification_id) {
    customMetrics['metric1'] = String(classification_id)
  }
  if (user_defined_brand_name) {
    customMetrics['metric2'] = String(user_defined_brand_name)
  }
  if (do_not_use !== '') {
    customMetrics['metric3'] = String(do_not_use)
  }
  if (keywords.length) {
    customMetrics['metric4'] = keywords.length
  }
  if (shot_types.length) {
    customMetrics['metric5'] = shot_types.length
  }
  if (subject.length) {
    customMetrics['metric6'] = subject.length
  }
  const fireflyGlobalData = {
    ...fireflyCommonData,
    customMetrics,
    customInteraction,
  }
  sendFireflyEvent(authData, trackingData, fireflyGlobalData)
  if (eventName !== BetaAnalytics.event.BULK_UPDATE_METADATA) {
    resetTaskId()
  }
}

export const trackBulkUpdateMetadata = ({
  authData = {},
  bulkMetadataPayload = [],
}) => {
  const trackingData = {
    eventType: BetaAnalytics.event.ASSET_USAGE,
    eventName: BetaAnalytics.event.BULK_UPDATE_METADATA,
  }
  bulkMetadataPayload.forEach((singleMetadata, index) => {
    trackUpdateMetadata({
      authData,
      metadataPayload: singleMetadata,
      trackingData,
    })
    if (index === bulkMetadataPayload.length - 1) {
      resetTaskId()
    }
  })
}

export const trackSearchInsights = ({ auth, requestPayload, response }) => {
  const fireflyCommonData = getCommonFireflyData(auth)
  const trackingData = {
    eventType: prevEventType ?? BetaAnalytics.event.SEARCH_RESPONSE_INSIGHTS,
    eventName: BetaAnalytics.event.SEARCH_RESPONSE_INSIGHTS,
  }
  const { page = '', rowsPerPage = '', payload = {} } = requestPayload
  const {
    search_term = '',
    search_type = '',
    sort_fields = {},
    template_id = '',
    filters = {},
  } = payload
  let customMetrics = {}
  customMetrics['metric1'] = String(search_type)
  customMetrics['metric2'] = String(search_term)
  customMetrics['metric3'] = JSON.stringify(sort_fields)
  customMetrics['metric4'] = Number(page)
  customMetrics['metric5'] = Number(rowsPerPage)
  fireflyCommonData.user['customDimension2'] = template_id
  const customInteraction = {
    key: 'total_results',
    value: response?.total_results || Number(0),
    location: JSON.stringify(filters),
  }
  const fireflyGlobalData = {
    ...fireflyCommonData,
    customMetrics,
    customInteraction,
  }
  sendFireflyEvent(auth, trackingData, fireflyGlobalData)
}
